import React from "react";
import {
  Star,
  PlayCircle,
  InstagramLogo,
  User,
} from "@phosphor-icons/react";

// ---------- Real written testimonials (from batch review card) ----------
const TESTIMONIALS = [
  {
    name: "Nidhin Roy",
    rating: 4.7,
    text:
      "This course helped me reduce loss and plan my trade better.",
  },
  {
    name: "Din",
    rating: 4.6,
    text:
      "I was able to double my capital in a short period, something I never thought was possible earlier.",
  },
  {
    name: "Jabir",
    rating: 4.6,
    text:
      "My entire perspective on trading changed. It was truly an eye opening experience. Trades are very minimal compared to before.",
  },
  {
    name: "Ram",
    rating: 4.5,
    text:
      "Your insights and expertise and understanding in this domain and suggestions are extremely valuable in helping me refine and strengthen the overall approach.",
  },
];

// ---------- Instagram reel video reviews ----------
const REELS = [
  { id: "DVct-NpErZb", label: "Batch review · 01" },
  { id: "DVQN4ymkpwY", label: "Batch review · 02" },
  { id: "DTM-JIEEihG", label: "Batch review · 03" },
  { id: "DTKSHSrkkJ0", label: "Batch review · 04" },
  { id: "DSpHB1lkrl-", label: "Batch review · 05" },
];

function StarRow({ rating }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.4;
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => {
        const isHalf = i === full && half;
        const isFull = i < full;
        return (
          <Star
            key={i}
            size={14}
            weight={isFull || isHalf ? "fill" : "regular"}
            className={
              isFull || isHalf ? "text-[#D9381E]" : "text-[#D4D0C5]"
            }
          />
        );
      })}
      <span className="ml-1.5 font-mono-acc text-[11px] tracking-[0.1em] font-bold text-[#0A0A0A]">
        {rating.toFixed(1)}
        <span className="text-[#4A4A4A]">/5</span>
      </span>
    </div>
  );
}

export default function Testimonials() {
  const avg =
    TESTIMONIALS.reduce((s, t) => s + t.rating, 0) / TESTIMONIALS.length;

  return (
    <section
      data-testid="testimonials-section"
      className="py-20 lg:py-28 border-t border-[#D4D0C5]"
    >
      <div className="max-w-[1320px] mx-auto px-5 lg:px-10">
        <div className="flex items-end justify-between gap-6 mb-10 flex-col md:flex-row">
          <div>
            <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-4">
              06 / WHAT STUDENTS SAY
            </div>
            <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
              500+ traders.
              <br />
              System-first.
            </h2>
          </div>
          <div className="text-right">
            <div className="font-display font-black text-4xl text-[#D9381E]">
              {avg.toFixed(1)}<span className="text-2xl text-[#4A4A4A]">/5</span>
            </div>
            <div className="font-mono-acc text-[10px] tracking-[0.2em] text-[#4A4A4A] uppercase mt-1">
              Average rating · verified reviews
            </div>
          </div>
        </div>

        {/* Written reviews */}
        <div
          data-testid="testimonials-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-16"
        >
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              data-testid={`testimonial-${i}`}
              className="border border-[#0A0A0A] bg-[#0A0A0A] text-[#F4F3ED] p-6 flex flex-col hover-brutal"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full bg-[#F4F3ED]/10 flex items-center justify-center overflow-hidden border border-[#F4F3ED]/20 shrink-0">
                  <User size={22} weight="duotone" className="text-[#F4F3ED]/70" />
                </div>
                <div>
                  <div className="font-display font-bold text-lg leading-tight">
                    {t.name}
                  </div>
                  <StarRow rating={t.rating} />
                </div>
              </div>
              <p className="font-body text-sm text-[#F4F3ED]/90 leading-relaxed flex-1">
                “{t.text}”
              </p>
            </div>
          ))}
        </div>

        {/* Video reviews (Instagram) */}
        <div data-testid="video-reviews">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
            <div>
              <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-3">
                Video reviews · Instagram
              </div>
              <h3 className="font-display font-black text-2xl lg:text-3xl tracking-tighter">
                Hear it from our students.
              </h3>
            </div>
            <a
              href="https://www.instagram.com/mallumarketprofile"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="testimonials-ig-follow"
              className="inline-flex items-center gap-2 border border-[#0A0A0A] px-4 py-2.5 font-mono-acc text-[10px] tracking-[0.25em] uppercase hover:bg-[#0A0A0A] hover:text-[#F4F3ED] transition-colors"
            >
              <InstagramLogo size={14} weight="fill" /> @mallumarketprofile
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
            {REELS.map((r) => (
              <a
                key={r.id}
                href={`https://www.instagram.com/reel/${r.id}/`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`reel-${r.id}`}
                className="group relative aspect-[9/16] block bg-[#0A0A0A] text-[#F4F3ED] overflow-hidden border border-[#0A0A0A]"
              >
                {/* Gradient background stand-in — Instagram blocks direct thumbnail hotlinks */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#D9381E]/25 via-[#0A0A0A] to-[#4B2E83]/40" />
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                  <PlayCircle
                    size={52}
                    weight="fill"
                    className="text-[#F4F3ED] group-hover:text-[#D9381E] transition-colors mb-2 drop-shadow-lg"
                  />
                  <div className="font-mono-acc text-[10px] tracking-[0.28em] uppercase text-[#F4F3ED]/80">
                    {r.label}
                  </div>
                </div>
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#F4F3ED]/95 text-[#0A0A0A] px-2 py-1">
                  <InstagramLogo size={11} weight="fill" className="text-[#D9381E]" />
                  <span className="font-mono-acc text-[9px] tracking-[0.15em] font-bold uppercase">
                    Reel
                  </span>
                </div>
                <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 bg-[#0A0A0A]/70 backdrop-blur-sm text-[#F4F3ED] px-2 py-1 font-mono-acc text-[9px] tracking-[0.15em] uppercase group-hover:bg-[#D9381E] transition-colors">
                  Watch →
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
